import React, { useEffect } from "react";
import style from "./TermsAndConditions.module.css";
import Icon from "@/ui/icon/Icon";
import Container from "@/layouts/container/Container";
import { Link } from "react-router-dom";
import Logo from "@/ui/logo/Logo";

// `notice` is only passed when an English reader opens this page from the English
// translation; Bosnian readers get no note.
function TermsAndConditions({ notice }) {
	useEffect(() => {
		const handleScroll = () => {
			const sections = document.querySelectorAll("[data-section]");
			const navLinks = document.querySelectorAll(`.${style.tocLink}`);

			// Find which section is in view
			let currentSection = "";
			sections.forEach((section) => {
				const sectionTop = section.offsetTop;
				const sectionHeight = section.clientHeight;
				if (
					window.scrollY >= sectionTop - 100 &&
					window.scrollY < sectionTop + sectionHeight - 100
				) {
					currentSection = section.getAttribute("data-section");
				}
			});

			// Update active class on links
			navLinks.forEach((link) => {
				const href = link.getAttribute("href");
				if (href) {
					const sectionId = href.replace("#", "");
					link.classList.toggle(
						style.active,
						sectionId === currentSection
					);
				}
			});
		};

		window.addEventListener("scroll", handleScroll);
		handleScroll(); // Initial check on mount

		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	return (
		<Container size="medium">
			<div className={style.grid}>
				<nav className={style.tableOfContents}>
					<div className={style.logo}>
						<Logo />
					</div>
					<ul>
						<li>
							<a href="#general" className={style.tocLink}>
								Generalno
							</a>
						</li>
						<li>
							<a
								href="#digital-content"
								className={style.tocLink}
							>
								Digitalni sadržaj
							</a>
						</li>
						<li>
							<a href="#payment" className={style.tocLink}>
								Načini plaćanja
							</a>
						</li>
						<li>
							<a href="#customers" className={style.tocLink}>
								Kupci
							</a>
						</li>
						<li>
							<a
								href="#usage-limitations"
								className={style.tocLink}
							>
								Ograničenja upotrebe
							</a>
						</li>
						<li>
							<a href="#warranty" className={style.tocLink}>
								Odricanje od garancije
							</a>
						</li>
						<li>
							<a href="#monitoring" className={style.tocLink}>
								Monitoring
							</a>
						</li>
						<li>
							<a
								href="#indemnification"
								className={style.tocLink}
							>
								Obeštećenje
							</a>
						</li>
						<li>
							<a href="#trademarks" className={style.tocLink}>
								Zaštitni znakovi
							</a>
						</li>
						<li>
							<a href="#third-party" className={style.tocLink}>
								Sadržaj treće strane
							</a>
						</li>
						<li>
							<a href="#educational" className={style.tocLink}>
								Obrazovni materijali i kvalifikacije
							</a>
						</li>
						<li>
							<a href="#disputes" className={style.tocLink}>
								Rješavanje sporova
							</a>
						</li>
					</ul>
				</nav>

				<div className={`${style.container} prose`}>
					{notice}

					<div className={style.header}>
						<div className={style.subHeader}>
							<span>Uslovi korištenja</span>
							<div className={style.nav}>
								<Link to="../">
									<Icon icon="fa-arrow-left" />
									<span>Nazad</span>
								</Link>
								<Icon icon="fa-slash-forward" />
								<Link to="/privacy-policy">
									<span>Politika privatnosti</span>
									<Icon icon="fa-arrow-right" />
								</Link>
							</div>
						</div>

						<h1>Naši uslovi korištenja</h1>
						<p>
							Molimo Vas, pažljivo pročitajte Uslove
							kori&scaron;tenja. Kori&scaron;tenjem i
							posjećivanjem stranice{" "}
							<a href="https://dekamas.com">
								https://dekamas.com
							</a>{" "}
							, podrazumijeva se da svjesno prihvatate ove Uslove
							kori&scaron;tenja. Ukoliko se ne slažete sa
							napisanim uslovima, možete u svakom trenutku
							prekinuti pristup web stranici. Kao vlasnici
							stranice{" "}
							<a href="https://dekamas.com">
								https://dekamas.com
							</a>
							, zadržavamo pravo da prema vlastitom nahođenju i
							potrebi, izmijenimo ili na neki drugi način
							ažuriramo Uslove kori&scaron;tenja u bilo koje
							vrijeme, te da ćete vi biti saglasni sa tim
							izmjenama i ažuriranjima.{" "}
						</p>
						<div className={style.date}>
							<div className={style.line}></div>
							<span>Ažurirano:</span>
							<span>April 12, 2025</span>
						</div>
					</div>

					<section id="general" data-section="general">
						<ol start="1">
							<li>
								<h2>Generalno</h2>
							</li>
						</ol>
						<p>
							Dobrodo&scaron;li na Dekamas online platformu koja
							korisnicima omogućava pristup interaktivnim
							obrazovnim kursevima koji se mogu pohađati putem
							vi&scaron;e elektronskih uređaja. Platforma nudi
							personalizirane video lekcije i interaktivno
							obrazovno iskustvo uz podr&scaron;ku AI asistenta.
							Registracijom, pristupanjem i/ili kori&scaron;tenjem
							na&scaron;e usluge, potvrđujete da ste pročitali,
							razumjeli i da ste saglasni sa ovim Uslovima
							kori&scaron;tenja, bez obzira da li ste korisnik
							usluge ili ne. Ovla&scaron;teni ste za
							kori&scaron;tenje usluge samo ako ćete se
							pridržavati svih primjenjivih zakona i ovih uslova.
							Dekamas zadržava pravo, prema vlastitom nahođenju,
							da promijeni, modificira, doda ili izbri&scaron;e
							dijelove ovih Uslova kori&scaron;tenja u bilo koje
							vrijeme bez daljnjeg upozorenja. Va&scaron;e daljnje
							kori&scaron;tenje usluge nakon bilo kakvih promjena
							predstavlja prihvatanje revidiranih uslova i
							odredbi.{" "}
						</p>
						<p>
							Ako se u bilo kojem trenutku ne pridržavate ovih
							Uslova kori&scaron;tenja, Dekamas zadržava pravo da
							odmah otkaže ili ukine va&scaron; pristup platformi
							(ili bilo kojem njenom dijelu) i/ili va&scaron;em
							korisničkom računu, ako postoji. Po vlastitom
							nahođenju i bez prethodne najave ili odgovornosti,
							Dekamas može prekinuti, modificirati ili izmijeniti
							bilo koji aspekt usluge uključujući, ali ne
							ograničavajući se na:{" "}
						</p>
						<ul>
							<li>
								ograničavanje vremena kada je platforma
								dostupna;{" "}
							</li>
							<li>
								ograničavanje količine dozvoljene upotrebe;{" "}
							</li>
							<li>
								ograničavanje ili ukidanje prava bilo kojeg
								korisnika na kori&scaron;tenje usluge.{" "}
							</li>
						</ul>
						<p>
							Saglasni ste da se svaki prekid ili otkazivanje
							va&scaron;eg pristupa ili kori&scaron;tenja
							platforme može izvr&scaron;iti bez prethodne najave.
							Ako se ne pridržavate uslova ovog Ugovora, slažete
							se da možemo odmah deaktivirati ili izbrisati
							va&scaron; korisnički račun, ako postoji, i sve
							povezane informacije i/ili datoteke na va&scaron;em
							korisničkom računu i/ili zabraniti svaki dalji
							pristup takvim informacijama, datotekama i/ili
							njihovim dijelovima. Nadalje, slažete se da Dekamas
							neće biti odgovoran prema vama ili bilo kojoj trećoj
							strani za bilo kakav prekid ili otkazivanje
							va&scaron;eg pristupa ili kori&scaron;tenja usluge.
							Prihvaćate da je va&scaron;e jedino pravo u pogledu
							bilo kakvog nezadovoljstva, bilo kakvom
							modifikacijom ili prekidom usluge ili bilo koje
							politike ili prakse Dekamas-a u pružanju usluge,
							uključujući bez ograničenja bilo kakvu promjenu
							sadržaja, da prestanete koristiti uslugu i otkažete
							ili ukinete svoju pretplatu ili korisnički račun,
							prema potrebi.{" "}
						</p>
					</section>

					<section
						id="digital-content"
						data-section="digital-content"
					>
						<ol start="2">
							<li>
								<h2>Digitalni sadržaj</h2>
							</li>
						</ol>
						<p>
							Svi obrazovni kursevi i materijali na platformi
							(svaki, &bdquo;Digitalni sadržaj") su isključivo
							vlasni&scaron;tvo Dekamasa ili njegovih davalaca
							licence i za&scaron;tićeni su autorskim pravima i
							drugim zakonima o intelektualnom vlasni&scaron;tvu.
							Preuzimanje i pristup bilo kojem digitalnom sadržaju
							dostupni su samo Korisnicima i namijenjeni su samo
							za ličnu i nekomercijalnu upotrebu tih Korisnika.
							Bilo koja druga upotreba Digitalnog sadržaja
							preuzetog ili kojem se pristupa putem Usluge je
							strogo zabranjena. Korisnici ne smiju mijenjati,
							prenositi, objavljivati, sudjelovati u prijenosu ili
							prodaji, reproducirati, stvarati izvedena djela,
							distribuirati, izvoditi, prikazivati ili na bilo
							koji način iskori&scaron;tavati bilo koji sadržaj, u
							cijelosti ili djelomično. Preuzimanjem ili na drugi
							način pristupanjem digitalnom sadržaju sa usluge,
							korisnik ovim potvrđuje i slaže se sa ovim uslovima.{" "}
						</p>
						<p>
							Va&scaron;e kori&scaron;tenje Dekamas platforme
							podliježe pravilima koja su ovdje navedena, a
							Dekamas može s vremena na vrijeme modificirati ih na
							svojoj web stranici. Va&scaron;e daljnje
							kori&scaron;tenje usluge Dekamas predstavlja
							prihvatanje pravila.{" "}
						</p>
						<p>
							Dekamas korisnici mogu pristupiti određenom broju
							besplatnih kurseva ili svim dostupnim kursevima uz
							godi&scaron;nju pretplatu. Digitalni sadržaj
							dostupan korisnicima s godi&scaron;njom pretplatom
							uključuje sve kurseve na platformi, kao i mogućnost
							sticanja mikro-kvalifikacija s ECTS bodovima kroz
							modularne kurseve. Svaki korisnik također dobija
							pristup DIA (Dekamas Intelligent Assistant) AI
							asistentu, s određenim brojem mjesečnih tokena za
							komunikaciju koje može iskoristiti za
							personalizirano obrazovno iskustvo.{" "}
						</p>
						<p>
							Svoje članstvo možete otkazati koristeći dugme
							&bdquo;Otkaži moju pretplatu" na Postavkama računa u
							odjeljku "Pretplate", osim ako je drugačije navedeno
							u ovim uslovima prodaje, tj. ako otkažete svoje
							članstvo, nećete dobiti povrat bilo koje već plaćene
							članarine. Kada se va&scaron;e članstvo otkaže,
							pristup digitalnom sadržaju povezanom s va&scaron;im
							računom pretplate bit će ograničen na besplatne
							kurseve. Podaci na va&scaron;em računu
							(bilje&scaron;ke, zavr&scaron;eni kursevi, stečene
							mikro-kvalifikacije, i slično) mogu biti sačuvani
							ako i samo ako vi to naznačite prilikom otkazivanja
							svoje pretplate, tako da ukoliko se odlučite da
							ponovo koristite na&scaron;e usluge u budućnosti,
							možete nastaviti ondje gdje ste stali i dobiti
							pristup va&scaron;im prethodnim podacima.{" "}
						</p>
						<p>
							Ograničenja se ne primjenjuju na broj kurseva koje
							korisnik pretplate može istovremeno pohađati.
							Pretplatnici mogu pristupiti koliko god kurseva žele
							u datom momentu. Ograničenja se primjenjuju na broj
							uređaja koji istovremeno mogu biti prijavljeni preko
							jednog računa (1 račun na 3 različita uređaja je
							limit) i na mjesečni broj tokena za komunikaciju s
							DIA asistentom. Dekamas zadržava pravo da
							jednostrano promijeni ova pravila bez prethodne
							najave.{" "}
						</p>
						<p>
							Dekamas također može preduzeti korake da spriječi
							prijevaru, kao &scaron;to je ograničavanje broja
							kurseva kojima se može pristupiti u isto vrijeme i
							nadgledanje korisničkih naloga za bilo koju
							aktivnost koja može da kr&scaron;i ove Uslove. Ako
							Dekamas otkrije bilo koju vrstu prijevare, Dekamas
							zadržava pravo poduzeti prinudne mjere uključujući
							ukidanje ili suspenziju korisničkog računa.{" "}
						</p>
					</section>

					<section id="payment" data-section="payment">
						<ol start="3">
							<li>
								<h2>Načini plaćanja</h2>
							</li>
						</ol>
						<p>
							Prilikom kupovine, sistem koji procesuira uplate je
							"Stripe", i prilikom plaćanja, prihvata sljedeće
							kreditne kartice: Visa, MasterCard, American
							Express, Discover i SEPA Direct Debit.{" "}
						</p>
						<h3>Politika refundacije</h3>
						<p>
							Dekamas ne nudi refundaciju za već plaćene
							članarine, osim u izuzetnim slučajevima. Povrat
							novca može biti odobren u sljedećim situacijama:{" "}
						</p>
						<ul>
							<li>
								Ako je do&scaron;lo do dvostruke naplate zbog
								tehničke gre&scaron;ke.{" "}
							</li>
							<li>
								Ako korisnik nije mogao koristiti platformu zbog
								tehničkih problema koji su trajali duže od 7
								dana.{" "}
							</li>
							<li>
								Ako je korisnik slučajno izvr&scaron;io uplatu i
								podnio zahtjev za povrat unutar 48 sati od
								transakcije.{" "}
							</li>
						</ul>
						<p>
							Zahtjev za refundaciju se podnosi putem e-maila na
							dekamas@ibu.edu.ba uz dokaz o uplati. Povrat novca
							(ako se odobri) biće izvr&scaron;en istim načinom
							plaćanja u roku od 14 radnih dana.{" "}
						</p>
					</section>

					<section id="customers" data-section="customers">
						<ol start="4">
							<li>
								<h2>Kupci</h2>
							</li>
						</ol>
						<p>
							Platforma je otvorena za javnost, ali određeni
							dijelovi, komponente, sadržaj i karakteristike
							platforme dostupni su samo pojedincima koji sklope
							ugovor sa Dekamas za pružanje usluge, registracijom
							na Dekamas, kreiranjem korisničkog računa na
							platformi, i slaganjem sa ovim Uslovima
							kori&scaron;tenja i Dekamas politikom privatnosti u
							trenutku kada je Dekamas nalog kreiran. Sve dok
							pojedinac ostaje Korisnik i njegov račun je aktivan.{" "}
						</p>
						<p>
							U slučajevima kada Dekamas prima email adrese ili
							druge lične podatke zaposlenika od poslovnih
							partnera, ti podaci će se koristiti isključivo za
							kreiranje korisničkih računa i omogućavanje pristupa
							platformi. Dekamas garantuje da ovi podaci neće biti
							kori&scaron;teni za marketin&scaron;ke svrhe,
							analitiku ili dijeljeni s trećim stranama bez
							izričitog pristanka poslovnog partnera ili krajnjih
							korisnika.{" "}
						</p>
						<p>
							Korisnici će imati stalni pristup i moći će
							kontinuirano primati, između ostalog, sljedeće
							dodatne usluge i funkcionalnost iste:{" "}
						</p>
						<ul>
							<li>
								mogućnost pristupa nalogu Korisnika i
								kori&scaron;tenja Digitalnog sadržaja
								(uključujući spisak dostupnih kurseva) putem
								Dekamas platforme na vi&scaron;e uređaja,
								uključujući tablete, mobilne telefone i lične
								računare (&bdquo;Uređaji") i putem Dekamas web
								stranice;{" "}
							</li>
							<li>
								mogućnost pristupa, pregleda i pohađanja
								obrazovnih kurseva na Korisnikovim uređajima
								unutar korisničkog računa uz kori&scaron;tenje
								Dekamas platforme;{" "}
							</li>
							<li>
								neograničeno skladi&scaron;tenje podataka o
								napretku i zavr&scaron;enim kursevima na Dekamas
								sistemima, kojem se može pristupiti u svakom
								trenutku kada je Korisnik prijavljen na svoj
								nalog i koristeći Dekamas platformu;{" "}
							</li>
							<li>
								mogućnost pristupa određenim funkcijama unutar
								digitalnog sadržaja kao &scaron;to su
								bilje&scaron;ke, praćenje napretka, interakcija
								s DIA asistentom i pretraga sadržaja;{" "}
							</li>
							<li>
								pristup stečenim mikro-kvalifikacijama i ECTS
								bodovima.{" "}
							</li>
						</ul>
						<p>Ako ste korisnik tada se slažete sa sljedećim: </p>
						<ol start="1">
							<li>
								s obzirom na va&scaron;e kori&scaron;tenje
								usluge, izjavljujete da ste punoljetni u
								području u kojem živite ili da imate 16 ili
								vi&scaron;e godina uz izričit pismeni pristanak
								va&scaron;eg roditelja ili zakonskog staratelja.
								Također se slažete da:{" "}
							</li>
						</ol>
						<ol start="1">
							<li>
								pružite istinite, tačne, aktuelne i potpune
								informacije o sebi kako to zahtijeva bilo koji
								obrazac(i) za registraciju na platformi,
								uključujući va&scaron;u geografsku lokaciju i
								adresu za naplatu (&bdquo;Podaci o
								registraciji"); i{" "}
							</li>
							<li>
								(a)(b) održavate i brzo ažurirate podatke o
								registraciji kako bi bili istiniti, tačni,
								aktuelni i potpuni. Ako dostavite bilo koju
								informaciju koja je neistinita, netačna, nije
								aktuelna ili nepotpuna, Dekamas ima pravo
								suspendirati ili ukinuti va&scaron; račun i
								odbiti bilo kakvu trenutnu ili buduću upotrebu
								usluge (ili bilo kojeg njenog dijela);{" "}
							</li>
						</ol>
						<ol start="2">
							<li>
								Korisnici će dobiti podatke računa nakon
								zavr&scaron;etka procesa registracije na
								platformi. Vi ste odgovorni za održavanje
								povjerljivosti &scaron;ifre i naloga i u
								potpunosti ste odgovorni za sve aktivnosti koje
								se de&scaron;avaju pod va&scaron;om
								&scaron;ifrom ili nalogom. Pristajete da odmah
								obavijestite Dekamas o svakoj
								neovla&scaron;tenoj upotrebi va&scaron;e
								&scaron;ifre ili računa ili bilo kojem drugom
								kr&scaron;enju sigurnosti.{" "}
							</li>
						</ol>
						<p>
							Sve lične podatke koje navedete u obrascu za
							registraciju biće prikupljeni u skladu sa Dekamas
							Politikom privatnosti i u izričite svrhe navedeni u
							Dekamas Politici privatnosti.{" "}
						</p>
					</section>

					<section
						id="usage-limitations"
						data-section="usage-limitations"
					>
						<ol start="5">
							<li>
								<h2>Ograničenja upotrebe</h2>
							</li>
						</ol>
						<p>Ovi uslovi se odnose na sve korisnike usluge.</p>
						<ol start="1">
							<li>
								Uslugu ćete koristiti samo u zakonite svrhe.
								Nećete postavljati ili prenositi putem usluge
								bilo kakav materijal koji na bilo koji način
								kr&scaron;i prava drugih, (ili) je nezakonit,
								prijeteći, uvredljiv, klevetnički, zadire u
								privatnost ili prava javnosti, vulgaran, opscen,
								profan ili na drugi način neprihvatljiv, i (ili)
								podstiče pona&scaron;anje koje bi predstavljalo
								krivično djelo, dovodi do građanske odgovornosti
								ili na drugi način kr&scaron;i bilo koji zakon.
								Bez Dekamas-ovog izričitog prethodnog odobrenja,
								nećete objavljivati ili prenositi putem
								platforme bilo kakav materijal koji sadrži
								reklamiranje ili bilo kakvo traženje u vezi sa
								proizvodima ili uslugama. Nećete koristiti
								uslugu za ogla&scaron;avanje ili obavljanje bilo
								kakvog komercijalnog pozivanja, uključujući, bez
								ograničenja, nagovaranje korisnika da postanu
								pretplatnici drugih online obrazovnih usluga.
								Neće biti dozvoljeno svako va&scaron;e
								pona&scaron;anje koje prema Dekamas-ovom
								nahođenju ograničava ili sprečava bilo kog
								drugog korisnika da koristi platformu.{" "}
							</li>
							<li>
								Sav sadržaj na platformi, uključujući, ali ne
								ograničavajući se na dizajn, tekst, grafiku,
								obrazovne materijale, video sadržaje,
								informacije, aplikacije, softver, muziku, zvuk i
								druge datoteke, te njihov odabir i raspored, su
								vlasni&scaron;tvo Dekamas-a ili njegovih
								davalaca licence sa svim pravima zadržanim.
								Nijedan sadržaj web stranice ne smije se
								mijenjati, kopirati, distribuirati, uokviriti,
								umnožavati, ponovo objavljivati, preuzimati,
								prikazivati, postavljati, prenositi ili
								prodavati u bilo kojem obliku ili na bilo koji
								način, u cjelini ili djelomično, bez prethodnog
								pismenog odobrenja Dekamas-a. Pod uslovom da
								ispunjavate uslove za kori&scaron;tenje usluge i
								da ste podložni ovim Uslovima kori&scaron;tenja,
								dodjeljuje vam se ograničena licenca za pristup
								platformi i za preuzimanje ili &scaron;tampanje
								kopije bilo kog dijela platforme na koji imate
								pravilno stečen pristup isključivo za
								va&scaron;u ličnu, nekomercijalnu upotrebu. Ne
								smijete učitavati ili ponovo objavljivati
								sadržaj stranice na bilo kojoj internetskoj,
								intranet ili ekstranet stanici ili inkorporirati
								informacije u bilo koju drugu bazu podataka ili
								kompilaciju, a svaka druga upotreba sadržaja
								stranice je strogo zabranjena. Prethodno
								navedena licenca podliježe ovim Uslovima
								kori&scaron;tenja i ne uključuje
								kori&scaron;tenje bilo kakvog rudarenja
								podataka, robota ili sličnih metoda prikupljanja
								ili ekstrakcije podataka. Bilo koja upotreba
								platforme koja nije ovdje izričito
								ovla&scaron;tena, bez prethodne pismene dozvole
								Dekamas-a, strogo je zabranjena i ukida licencu
								koja je ovdje dodijeljena. Takvo
								neovla&scaron;teno kori&scaron;tenje također
								može predstavljati kr&scaron;enje primjenjivih
								zakona, uključujući bez ograničenja zakone o
								autorskim pravima i žigovima i primjenjive
								propise i statute o komunikacijama. Dekamas može
								opozvati ovu licencu u bilo koje vrijeme bez
								prethodne najave i sa ili bez razloga.{" "}
							</li>
							<li>
								Ne smijete prikrivati ili pogre&scaron;no
								predstavljati svoju geografsku lokaciju,
								krivotvoriti zaglavlja, koristiti proksije,
								koristiti lažnu IP adresu ili na drugi način
								manipulirati identifikatorima kako biste
								prikrili porijeklo bilo koje poruke ili
								prijenosa koje &scaron;aljete putem usluge. Ne
								smijete se pretvarati da jeste, ili da
								predstavljate nekoga drugog, niti se lažno
								predstavljati kao bilo koje drugo lice ili
								entitet.{" "}
							</li>
							<li>
								Zabranjeno vam je da kr&scaron;ite ili
								poku&scaron;avate naru&scaron;iti sigurnost
								platforme, uključujući, bez ograničenja:{" "}
							</li>
						</ol>
						<ol start="1">
							<li>
								pristup podacima koji nisu namijenjeni vama,
								uključujući prijavu na server ili račun za koji
								niste ovla&scaron;teni da pristupite;{" "}
							</li>
							<li>
								poku&scaron;aj ispitivanja, skeniranja ili
								testiranja ranjivosti sistema ili mreže radi
								naru&scaron;avanja mjera sigurnosti ili provjere
								autentičnosti bez odgovarajućeg
								ovla&scaron;tenja;{" "}
							</li>
							<li>
								kori&scaron;tenje bilo kojeg "dubokog linka",
								"scrape-a", "robota", "pauka" ili drugog
								automatskog uređaja, programa, algoritma ili
								metodologije, ili bilo kojeg sličnog ili
								ekvivalentnog ručnog procesa, za pristup,
								preuzimanje, kopiranje ili nadgledanje bilo
								kojeg dijela usluge ili bilo kojeg sadržaja
								stranice, ili na bilo koji način reproducirati
								ili zaobići navigacijsku strukturu ili
								prezentaciju usluge ili nekog dijela sadržaja
								stranice, kako bi se pribavili ili
								poku&scaron;ali dobiti bilo koji materijali,
								dokumenti ili informacije na bilo koji način
								koji nije namjerno stavljen na raspolaganje
								preko stranice;{" "}
							</li>
							<li>
								poduzimanje bilo koje radnje koja nameće
								nerazumno ili nesrazmjerno veliko opterećenje na
								infrastrukturu platforme ili Dekamas-ove sisteme
								ili mreže, ili bilo koje sisteme ili mreže
								povezane sa platformom ili Dekamas-om;{" "}
							</li>
							<li>
								provođenje obrnutog pretraživanja, praćenje ili
								traženje bilo kakvih informacija o bilo kom
								drugom korisniku, Kupcu ili posjetitelju usluge,
								ili nekom drugom korisniku Dekamas-a, do
								njihovog izvora, ili iskori&scaron;tavanje
								usluge ili informacija napravljene dostupno ili
								ponuđeno od strane ili putem usluge, na bilo
								koji način gdje je svrha otkrivanja bilo koje
								informacije, uključujući, ali ne ograničavajući
								se na ličnu identifikaciju ili informacije, osim
								va&scaron;ih vlastitih podataka, kako je
								predviđeno Uslugama;{" "}
							</li>
							<li>
								poku&scaron;aj ometanja ili onemogućavanja
								usluge bilo kom korisniku, hostu ili mreži,
								uključujući, bez ograničenja, napade
								"uskraćivanje usluge", preopterećenje, "spam
								po&scaron;te" ili "ru&scaron;enje";{" "}
							</li>
							<li>
								falsifikovanje zaglavlja bilo kojeg TCP/IP
								paketa ili bilo kojeg dijela informacija
								zaglavlja u bilo kojoj e-po&scaron;ti;{" "}
							</li>
							<li>
								ometanje mrežnih čvorova ili mrežne usluge ili
								na drugi način ograničavanje, inhibiranje,
								ometanje Dekamas-ove sposobnosti da nadgleda ili
								učini dostupnom Uslugu; ili{" "}
							</li>
							<li>
								preduzimanje bilo kakvih radnji u cilju
								dobijanja usluga na koje nemate pravo.{" "}
							</li>
						</ol>
						<p>
							Kr&scaron;enje sigurnosti sistema ili mreže može
							rezultirati građanskom ili krivičnom
							odgovorno&scaron;ću. Dekamas će istražiti događaje
							koji mogu uključivati takva kr&scaron;enja i može
							uključiti i sarađivati s organima za provođenje
							zakona u procesuiranju bilo kojeg korisnika (bez
							obzira da li je korisnik usluge ili ne) koji je
							umije&scaron;an u takva kr&scaron;enja.{" "}
						</p>
					</section>

					<section id="warranty" data-section="warranty">
						<ol start="6">
							<li>
								<h2>
									Odricanje od garancije; Ograničenje
									odgovornosti
								</h2>
							</li>
						</ol>
						<ol start="1">
							<li>
								IZRIČITO SE SLAŽETE DA JE KORI&Scaron;ĆENJE WEB
								SAJTA NA VA&Scaron; VLASTITI RIZIK. NITI
								DEKAMAS, NITI NJEGOVI SARADNICI NI BILO KO OD
								NJIHOVIH ZAPOSLENIH, AGENATA, DOBAVLJAČA
								SADRŽAJA, TREĆIH STRANA ILI DAVAOCA LICENCE NE
								GARANTUJE DA ĆE SAJT BITI BEZ GRE&Scaron;AKA;
								NITI DAJU GARANCIJU ZA REZULTATE KOJI SE MOGU
								DOBITI KORI&Scaron;ĆENJEM WEB SAJTA, ILI ZA
								TAČNOST ILI POUZDANOST BILO KAKVE INFORMACIJE,
								USLUGE ILI ROBE PRUŽENE PREKO WEB SAJTA.{" "}
							</li>
							<li>
								WEB SAJT JE OBEZBIJEĐEN NA OSNOVU &bdquo;TAKAV
								KAKAV JESTE" BEZ BILO KOJE VRSTE GARANCIJE,
								IZRIČITIH ILI PODRAZUMIJEVANIH, UKLJUČUJUĆI, ALI
								NE OGRANIČENO NA, GARANCIJE NA VLASNI&Scaron;TVO
								ILI IMPLICIRANE GARANCIJE KOJE SE ODNOSE NA
								OBLASTI TRGOVACA. DODATNO, NE POSTOJE NIKAKVE
								GARANCIJE ZA REZULTATE DOBIJENE
								KORI&Scaron;ĆENJEM WEB SAJTA.{" "}
							</li>
							<li>
								NI U KOJEM SLUČAJU, DEKAMAS, ILI BILO KOJA OSOBA
								ILI SUBJEKT UKLJUČEN U KREIRANJE, PROIZVODNJU
								ILI DISTRIBUCIJU WEB SAJTA ILI SADRŽAJA
								UKLJUČENOG U NJEGA, NEĆE BITI ODGOVORNA U
								UGOVORU, VAŽEĆI ZAKON MOŽDA NE DOZVOLJAVA
								OGRANIČENJE ILI ISKLJUČENJE ODGOVORNOSTI ILI
								SLUČAJNU ILI POSLJEDIČNU &Scaron;TETU, TAKO DA
								SE OGRANIČENJA ILI ISKLJUČENJE IZNAD MOŽDA NE
								ODNOSE NA VAS. NI U KOJEM SLUČAJU, DEKAMAS-OVA
								UKUPNA ODGOVORNOST PREMA VAMA ZA SVE
								&Scaron;TETE I GUBITKE NEĆE PRELAZITI UKUPNU
								SUMU KOJU STE VI PLATILI ZA PRISTUP WEB SAJTU.{" "}
							</li>
							<li>
								NAVEDENI UGOVOR ODRICANJA ODGOVORNOSTI
								PRIMJENJUJE SE NA BILO KAKVU &Scaron;TETU ILI
								POVREDU PROUZROKOVANU BILO KOJIM
								GRE&Scaron;KAMA, PROPUSTOM, NETOČNOSTIMA,
								PREKIDIMA, BRISANJEM, DEFEKTOM,
								KA&Scaron;NJENJEM U RADU, ILI KRETANJEM
								TRANSPORTNE LINIJE PRISTUP, PROMJENA ILI
								KORI&Scaron;ĆENJE OVOG SAJTA, BILO ZBOG
								KR&Scaron;ENJA UGOVORA, DJELATNOG
								PONA&Scaron;ANJA (UKLJUČUJUĆI STROGU
								ODGOVORNOST), NEMARA ILI POD BILO KOJIM DRUGIM
								UZROKOM RADNJE. IZRIČITO POTVRĐUJETE DA DEKAMAS
								NIJE ODGOVORAN ZA KLEVETNIČKO, UVRIJEDNO ILI
								NEZAKONITO PONA&Scaron;ANJE DRUGIH KORISNIKA ILI
								TREĆIH LICA I DA RIZIK OD POVREDE IZ NAVEDENOG
								OSTAJE CIJELO NA VAMA.{" "}
							</li>
						</ol>
					</section>

					<section id="monitoring" data-section="monitoring">
						<ol start="7">
							<li>
								<h2>Monitoring</h2>
							</li>
						</ol>
						<p>
							Dekamas ima pravo, ali ne i obavezu, da nadzire
							sadržaj platforme, da utvrdi usklađenost sa ovim
							Uslovima kori&scaron;tenja i svim operativnim
							pravilima koje je uspostavio Dekamas i da zadovolji
							bilo koji zakon, propis ili ovla&scaron;tenu vladu
							na zahtjev. Dekamas ima pravo po sopstvenom
							nahođenju da uređuje, odbije da objavi ili ukloni
							bilo koji materijal dostavljen ili objavljen na
							platformi. Bez ograničavanja gore navedenog, Dekamas
							ima pravo da ukloni bilo koji materijal za koji
							Dekamas po sopstvenom nahođenju utvrdi da
							kr&scaron;i odredbe ovih Uslova ili je na neki drugi
							način neprikladan. Iako Dekamas zadržava pravo da
							ukloni, bez prethodne najave, bilo koju objavu iz
							bilo kog razloga, Dekamas nema obavezu da
							izbri&scaron;e priloge koji su vam neprihvatljivi
							ili uvredljivi.{" "}
						</p>
					</section>

					<section
						id="indemnification"
						data-section="indemnification"
					>
						<ol start="8">
							<li>
								<h2>Obeštećenje</h2>
							</li>
						</ol>
						<p>
							Saglasni ste da ćete braniti i držati Dekamas i
							njegove podružnice i njihove odgovarajuće direktore,
							službenike, zaposlene i agente od i protiv svih
							potraživanja, radnji, zahtjeva, &scaron;teta,
							obaveza, gubitaka i tro&scaron;kova (uključujući
							razumne advokatske naknade) koji proizilaze iz
							va&scaron;eg kori&scaron;tenja Usluge.{" "}
						</p>
					</section>

					<section id="trademarks" data-section="trademarks">
						<ol start="9">
							<li>
								<h2>Zaštitni znakovi</h2>
							</li>
						</ol>
						<p>
							Dekamas, dekamas.com i srodne riječi, imena domena i
							logotipi su za&scaron;titni znakovi i
							vlasni&scaron;tvo Dekamas. Svi ostali
							za&scaron;titni znakovi, nazivi proizvoda i nazivi
							kompanija ili logotipi koji se ovdje citiraju
							vlasni&scaron;tvo su njihovih vlasnika.{" "}
						</p>
					</section>

					<section id="third-party" data-section="third-party">
						<ol start="10">
							<li>
								<h2>Sadržaj treće strane</h2>
							</li>
						</ol>
						<p>
							Sva mi&scaron;ljenja, savjeti, izjave, usluge,
							ponude ili druge informacije ili sadržaj izraženi
							ili dostupni od strane trećih strana, uključujući
							pružaoce sadržaja i korisnike, pripadaju
							odgovarajućim autorima ili distributerima, a ne
							Dekamas-u. U mnogim slučajevima, sadržaj dostupan
							putem platforme predstavlja mi&scaron;ljenja i
							prosudbe odgovarajućeg provajdera sadržaja ili
							korisnika. Dekamas niti podržava niti snosi
							odgovornost za tačnost ili pouzdanost bilo kakvog
							mi&scaron;ljenja, savjeta ili izjava.{" "}
						</p>
					</section>

					<section id="educational" data-section="educational">
						<ol start="11">
							<li>
								<h2>Obrazovni materijali i kvalifikacije</h2>
							</li>
						</ol>
						<p>
							Svi obrazovni materijali, kursevi i sadržaji koji se
							nude na Dekamas platformi su razvijeni i
							verifikovani od strane kvalifikovanih stručnjaka i
							predavača. Dekamas osigurava da su
							mikro-kvalifikacije i ECTS bodovi koji se dodjeljuju
							kroz platformu u skladu s relevantnim obrazovnim
							standardima i akreditacijama. Međutim, korisnici su
							odgovorni za provjeru priznavanja stečenih
							kvalifikacija i ECTS bodova u svojim obrazovnim
							institucijama ili zemljama prebivali&scaron;ta.
							Dekamas nije odgovoran za bilo kakve
							pote&scaron;koće koje mogu nastati prilikom
							priznavanja stečenih kvalifikacija u različitim
							obrazovnim sistemima ili institucijama.{" "}
						</p>
						<p>
							Svi obrazovni materijali na platformi su
							vlasni&scaron;tvo Dekamasa ili njegovih partnera i
							za&scaron;tićeni su autorskim pravima. Korisnici
							mogu pristupiti i koristiti ove materijale
							isključivo u svrhu vlastitog obrazovanja i u skladu
							s ovim Uslovima kori&scaron;tenja. Svako
							neovla&scaron;teno kopiranje, distribucija ili
							kori&scaron;tenje obrazovnih materijala je strogo
							zabranjeno.{" "}
						</p>
						<p>
							Dekamas zadržava pravo da u bilo kojem trenutku
							izmijeni sadržaj kurseva, kriterije za dobijanje
							mikro-kvalifikacija ili broj dodijeljenih ECTS
							bodova, u skladu s promjenama u obrazovnim
							standardima ili potrebama platforme.{" "}
						</p>
						<h3>Priznavanje mikro-kvalifikacija i ECTS bodova</h3>
						<p>
							Mikro-kvalifikacije i ECTS bodovi stečeni putem
							Dekamas platforme dodjeljuju se u saradnji s
							International Burch University (IBU).{" "}
						</p>
						<p>
							Ove kvalifikacije su priznate od strane Ministarstva
							za odgoj i obrazovanje Kantona Sarajevo. Međutim,
							njihovo priznavanje u drugim institucijama ili
							državama zavisi od internih pravila svake obrazovne
							ustanove.{" "}
						</p>
						<p>
							Preporučujemo korisnicima da prije upisa provjere da
							li njihova matična institucija priznaje ECTS bodove
							stečene putem Dekamas platforme. Dekamas ne
							garantuje automatsko priznavanje u svim akademskim
							ili profesionalnim sistemima.{" "}
						</p>
					</section>

					<section id="disputes" data-section="disputes">
						<ol start="12">
							<li>
								<h2>Rješavanje sporova</h2>
							</li>
						</ol>
						<p>
							U slučaju bilo kakvog spora ili nesuglasica između
							korisnika i Dekamasa, strane će prvo poku&scaron;ati
							rije&scaron;iti problem mirnim putem, putem e-mail
							adrese dekamas@ibu.edu.ba.{" "}
						</p>
						<p>
							Ako se spor ne rije&scaron;i u roku od 30 dana,
							korisnici i Dekamas se slažu da će se spor
							rje&scaron;avati arbitražom pred Stalnim izabranim
							sudom pri Privrednoj komori Federacije Bosne i
							Hercegovine u Sarajevu, u skladu s njegovim
							pravilima.{" "}
						</p>
						<p>
							Arbitraža će se voditi na bosanskom jeziku, a
							tro&scaron;kove postupka snosi strana koja ga je
							inicirala, osim ako arbitražni sud ne odluči
							drugačije.{" "}
						</p>
						<p>
							U slučaju da arbitraža nije moguća ili nije
							prihvatljiva za korisnika, nadležan je Općinski sud
							u Sarajevu.{" "}
						</p>
					</section>
				</div>
			</div>
		</Container>
	);
}

export default TermsAndConditions;
